import React, { useState, useEffect, createContext, FC, ReactNode, useContext, ReactElement } from 'react'
import { api } from './network/api'

import { Alert, Link, Typography } from "@mui/material"
import { Navigate } from "react-router-dom"
import Cookies from "js-cookie"
import { HeroData } from './heropage/buildsys'
import { TeamData } from './teampage/team'



// 定义 data 的类型
export type GameData = {
    id: number
    name: string;
    [ key: string ]: any
}

export interface GameDataContextProps {
    getName: ( comp: CompName, id: number ) => ReactElement
    getNameString: ( comp: CompName, id: number ) => string
    getData: ( comp: CompName, id: number ) => GameData
    getList: ( comp: CompName ) => GameData[]
}

export enum CompName {
    skill = "skill",
    attr = "attr",
    action = "action",
    effect = "effect",
    resource = "resource",
    race = "race",
    profession = "profession",
    mission = "mission",
    enemy = "enemy",
    npc = "npc",
    position = "pos",
    area = "area",
    equipment = "eq",
    equipmentType = "eqtype",
    equipmentPart = "eqpart",

    tilemap = "map",
}

const supportInfo = [ CompName.skill ]

export const GameDataContext = createContext<GameDataContextProps | undefined>( undefined )

// 缓存白名单：hero 等已从 CompName 移除的键，加载时直接丢掉（旧缓存不会再被渲染/回写）
const KNOWN_COMPONENT_KEYS: string[] = Object.values( CompName )

function loadStoredGameDatas (): { [ key: string ]: GameData[] } {
    const storedGameDatas = localStorage.getItem( "gameDatas" )
    if ( !storedGameDatas ) {
        return {}
    }
    try {
        const parsed = JSON.parse( storedGameDatas )
        if ( !parsed || typeof parsed !== 'object' ) {
            return {}
        }
        const pruned: { [ key: string ]: GameData[] } = {}
        KNOWN_COMPONENT_KEYS.forEach( ( key ) => {
            if ( Array.isArray( parsed[ key ] ) ) {
                pruned[ key ] = parsed[ key ]
            }
        } )
        return pruned
    } catch ( error ) {
        // 缓存损坏（手动改坏/写入中断）不应白屏
        console.error( 'Error parsing cached gameDatas, dropped:', error )
        localStorage.removeItem( 'gameDatas' )
        return {}
    }
}

export const GameDataProvider: FC<{ children: ReactNode }> = ( { children } ) => {
    const [ gameDatas, setGameDatas ] = useState<{ [ key: string ]: GameData[] }>( loadStoredGameDatas )

    useEffect( () => {
        const fetchData = async () => {
            // 全部组件拉取完成后一次性 setState（逐条 set 会触发 N 次全应用重渲染 + N 次 localStorage 全量写入，刷新慢）
            const results = await Promise.all( Object.values( CompName ).map( async ( name ) => {
                try {
                    const response = await api.get( `/component/` + name )
                    const income: { [ key: string ]: GameData } = response.data
                    return [ name, Array.from( Object.values( income ) ) ] as const
                } catch ( error ) {
                    console.error( `Error getting ${ name } component data:`, error )
                    return null
                }
            } ) )
            const merged: { [ key: string ]: GameData[] } = {}
            results.filter( Boolean ).forEach( ( r ) => {
                if ( r ) merged[ r[ 0 ] ] = r[ 1 ]
            } )
            setGameDatas( ( prevGameDatas ) => ( { ...prevGameDatas, ...merged } ) )
        }

        fetchData()
    }, [] )


    // 将 gameDatas 存储到 localStorage
    useEffect( () => {
        if ( gameDatas !== null ) {
            localStorage.setItem( "gameDatas", JSON.stringify( gameDatas ) )
        } else {
            localStorage.removeItem( 'gameDatas' )
        }
    }, [ gameDatas ] )

    const getName = function ( comp: CompName, id: number ): ReactElement {
        let items = gameDatas[ comp ]
        if ( !items ) {
            return <Typography> { "未找到" + comp } </Typography>
        }
        let res = items.find( ( item ) => item.id === id )
        if ( !res ) {
            return <Typography> { "未找到" + comp + ":" + id.toString() } </Typography>
        }
        if ( supportInfo.includes( comp ) ) {
            return <Link href={ '/详细信息/' + comp + '/' + res.name }>{ res.name } </Link>
        }
        // console.log( comp )
        // console.log( supportInfo )
        return <Typography>{ res.name } </Typography>
    }
    const getNameString = function ( comp: CompName, id: number ): string {
        let items = gameDatas[ comp ]
        if ( !items ) {
            return "未找到" + comp
        }
        let res = items.find( ( item ) => item.id === id )
        if ( !res ) {
            return "未找到" + comp + ":" + id.toString()
        }
        return res.name
    }
    const getData = function ( comp: CompName, id: number ): GameData {
        let items = gameDatas[ comp ]
        if ( !items ) {
            return { id: 0, name: "未找到" + comp }
        }
        let res = items.find( ( item ) => item.id === id )
        if ( !res ) {
            return { id: 0, name: "未找到" + comp + ":" + id.toString() }
        }
        return res
    }
    const getList = function ( comp: CompName ): GameData[] {
        return gameDatas[ comp ]
    }

    return (
        <GameDataContext.Provider value={ { getName, getData, getList, getNameString } }>
            { children }
        </GameDataContext.Provider>
    )
}
